// Conversation UI Refresh Styles
// Modern, clean design system for the therapist conversation interface
// Uses MUI sx-compatible tokens where possible

export const conversationUIStyles = {
  // Layout containers
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    backgroundColor: 'grey.50',
    overflow: 'hidden'
  },

  // Top Control Bar
  topControlBar: {
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 3,
    py: 0,
    backgroundColor: 'background.paper',
    borderBottom: 1,
    borderColor: 'divider',
    boxShadow: 1,
    zIndex: 'appBar'
  },

  modeSelector: {
    display: 'flex',
    backgroundColor: 'grey.100',
    borderRadius: 3,
    p: 0.5,
    gap: 0.5
  },

  modeButton: {
    py: 1,
    px: 2,
    borderRadius: 2,
    border: 'none',
    backgroundColor: 'transparent',
    color: 'text.secondary',
    fontWeight: 500,
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    '&:hover': {
      backgroundColor: 'action.hover'
    }
  },

  modeButtonActive: {
    ai: {
      backgroundColor: 'info.main',
      color: 'info.contrastText',
      boxShadow: '0 2px 8px rgba(33,150,243,0.3)'
    },
    human: {
      backgroundColor: 'success.main',
      color: 'success.contrastText',
      boxShadow: '0 2px 8px rgba(76,175,80,0.3)'
    },
    assisted: {
      backgroundColor: 'warning.main',
      color: 'warning.contrastText',
      boxShadow: '0 2px 8px rgba(255,152,0,0.3)'
    }
  },

  statusDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flex: 1,
    justifyContent: 'center'
  },

  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    py: 0.5,
    px: 1.5,
    borderRadius: '20px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    backgroundColor: 'action.hover',
    color: 'text.secondary'
  },

  actionButtons: {
    display: 'flex',
    gap: 1
  },

  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 2.5,
    border: 'none',
    backgroundColor: 'background.paper',
    color: 'text.secondary',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'grey.100',
      transform: 'translateY(-1px)',
      boxShadow: 2
    }
  },

  // Content Area
  contentArea: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    position: 'relative'
  },

  // Sidebar
  sidebar: {
    width: 300,
    backgroundColor: 'background.paper',
    borderRight: 1,
    borderColor: 'divider',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 'mobileStepper'
  },

  sidebarCollapsed: {
    width: 48
  },

  sidebarToggle: {
    position: 'absolute',
    right: -20,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    boxShadow: 2,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    zIndex: 'mobileStepper',
    '&:hover': {
      transform: 'translateY(-50%) scale(1.1)',
      boxShadow: 4
    }
  },

  sidebarContent: {
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflow: 'auto'
  },

  sidebarCard: {
    backgroundColor: 'background.paper',
    borderRadius: 3,
    p: 2,
    border: 1,
    borderColor: 'divider',
    boxShadow: 1,
    transition: 'all 0.2s',
    '&:hover': {
      boxShadow: 3,
      transform: 'translateY(-1px)'
    }
  },

  sidebarCardTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'text.primary',
    mb: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },

  // Message Area
  messageArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'background.paper',
    position: 'relative',
    overflow: 'hidden'
  },

  messageContainer: {
    flex: 1,
    overflowY: 'auto',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    scrollBehavior: 'smooth'
  },

  messageBubble: {
    maxWidth: '70%',
    py: 1.5,
    px: 2,
    borderRadius: 4,
    position: 'relative',
    animation: 'fadeInUp 0.3s ease-out',
    backgroundColor: 'grey.50',
    boxShadow: 1
  },

  clientMessage: {
    alignSelf: 'flex-start',
    background: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
    color: '#4A148C',
    border: '1px solid rgba(156, 39, 176, 0.2)',
    borderBottomLeftRadius: '4px'
  },

  therapistMessage: {
    alignSelf: 'flex-end',
    background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
    color: '#E65100',
    border: '1px solid rgba(255, 140, 0, 0.2)',
    borderBottomRightRadius: '4px'
  },

  aiTherapistMessage: {
    alignSelf: 'flex-end',
    background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
    color: '#0D47A1',
    border: '1px solid rgba(33, 150, 243, 0.2)',
    borderBottomRightRadius: '4px'
  },

  aiMessage: {
    alignSelf: 'flex-start',
    background: 'linear-gradient(135deg, #E3F2FD 0%, #F5F5F5 100%)',
    border: '1px solid rgba(33, 150, 243, 0.3)'
  },

  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 0.5,
    fontSize: '0.75rem',
    color: 'text.secondary'
  },

  modeIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    py: 0.25,
    px: 1,
    borderRadius: 3,
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase'
  },

  // AI Suggestion Overlay
  suggestionOverlay: {
    position: 'absolute',
    bottom: 100,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '90%',
    maxWidth: 600,
    backgroundColor: 'background.paper',
    borderRadius: 4,
    boxShadow: 10,
    border: '1px solid rgba(255,152,0,0.2)',
    p: 2.5,
    zIndex: 'speedDial',
    animation: 'slideUp 0.3s ease-out'
  },

  suggestionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 1.5
  },

  suggestionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'warning.main'
  },

  suggestionContent: {
    p: 1.5,
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
    borderRadius: 2,
    mb: 2,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: 'text.primary'
  },

  suggestionActions: {
    display: 'flex',
    gap: 1,
    justifyContent: 'flex-end'
  },

  // Bottom Input Bar
  inputBar: {
    display: 'flex',
    alignItems: 'center',
    py: 2,
    px: 3,
    backgroundColor: 'background.paper',
    borderTop: 1,
    borderColor: 'divider',
    gap: 1.5,
    minHeight: 80
  },

  inputField: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'grey.50',
    borderRadius: 6,
    px: 2.5,
    border: '2px solid transparent',
    transition: 'all 0.2s',
    '&:focus-within': {
      backgroundColor: 'background.paper',
      borderColor: 'primary.main',
      boxShadow: '0 0 0 3px rgba(255,140,0,0.1)'
    }
  },

  textInput: {
    flex: 1,
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
    fontSize: '0.875rem',
    py: 1.5,
    px: 0,
    color: 'text.primary'
  },

  inputActions: {
    display: 'flex',
    gap: 0.5
  },

  sendButton: {
    py: 1.5,
    px: 3,
    borderRadius: 6,
    border: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: 'common.white'
  },

  sendButtonMode: {
    ai: {
      background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
      boxShadow: '0 4px 12px rgba(33,150,243,0.3)'
    },
    human: {
      background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
      boxShadow: '0 4px 12px rgba(76,175,80,0.3)'
    },
    assisted: {
      background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
      boxShadow: '0 4px 12px rgba(255,152,0,0.3)'
    }
  },

  // Floating elements
  scrollToBottom: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'background.paper',
    boxShadow: 4,
    border: 1,
    borderColor: 'divider',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: 6
    }
  },

  // Slide-out panels
  slidePanel: {
    position: 'fixed',
    top: 64,
    right: 0,
    bottom: 0,
    width: 400,
    backgroundColor: 'background.paper',
    boxShadow: 8,
    transform: 'translateX(100%)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 'tooltip',  // Must be above backdrop ('modal'/1300) so clicks reach panel content
    display: 'flex',
    flexDirection: 'column'
  },

  slidePanelOpen: {
    transform: 'translateX(0)'
  },

  slidePanelHeader: {
    p: 2.5,
    borderBottom: 1,
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },

  slidePanelContent: {
    flex: 1,
    overflow: 'auto',
    p: 2.5
  },

  // Backdrop for modals/panels
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 'modal',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.3s, visibility 0.3s'
  },

  backdropVisible: {
    opacity: 1,
    visibility: 'visible'
  }
};

// CSS animations
const animations = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
`;

// Inject animations
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = animations;
  document.head.appendChild(styleSheet);
}

export default conversationUIStyles;
